/**
 * Entitlement limit helpers.
 *
 * Numeric entitlements returned by `/subscription/current-plan` use `-1` as the
 * "Unlimited" sentinel. Anything >= 0 is a hard cap (0 = feature unavailable,
 * e.g. FOC `zap_link_limit`).
 *
 * Route every limit comparison / label through these helpers so a `-1` is never
 * treated as a cap of minus one (which would block everything).
 */

export const UNLIMITED_ENTITLEMENT = -1;

/** true when the entitlement value means "no cap". */
export const isUnlimitedEntitlement = (value: any): boolean => {
  const n = Number(value);
  return Number.isFinite(n) && n < 0;
};

/** Normalises a raw entitlement value to a number (missing/NaN -> fallback). */
export const entitlementLimit = (value: any, fallback = 0): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/** true when `used` has already consumed the whole allowance. Unlimited never does. */
export const isLimitReached = (used: number, limit: any): boolean => {
  if (isUnlimitedEntitlement(limit)) return false;
  return Number(used || 0) >= entitlementLimit(limit, 0);
};

/** true when a projected total would go past the allowance. Unlimited never does. */
export const exceedsLimit = (projectedTotal: number, limit: any): boolean => {
  if (isUnlimitedEntitlement(limit)) return false;
  return Number(projectedTotal || 0) > entitlementLimit(limit, 0);
};

/**
 * Turns the raw value into a number usable in `<`/`>` maths.
 * Unlimited -> Infinity, so existing comparisons stay correct.
 */
export const limitAsNumber = (value: any, fallback = 0): number => {
  if (isUnlimitedEntitlement(value)) return Infinity;
  return entitlementLimit(value, fallback);
};

/** Human-readable limit for labels and error copy ("Unlimited" / "1,000"). */
export const formatEntitlementLimit = (value: any): string => {
  if (isUnlimitedEntitlement(value)) return 'Unlimited';
  return entitlementLimit(value, 0).toLocaleString();
};

/**
 * true when the feature is switched off entirely (cap of 0).
 * Unlimited and any positive cap are "available".
 */
export const isFeatureDisabledByLimit = (value: any): boolean =>
  !isUnlimitedEntitlement(value) && entitlementLimit(value, 0) <= 0;

/* ---------------------------------------------------------------------
 * Boolean / set entitlements
 * ------------------------------------------------------------------- */

/**
 * true when the plan allows catch-all ("Any Post" / "Any Story") automations,
 * i.e. an automation saved with scope = 'all' for a post/story zap.
 *
 * Backed by the `any_media_automation_enabled` boolean entitlement.
 * FOC/Basic is false; Trial and every paid plan are true.
 *
 * A missing key deliberately reads as FALSE: if entitlements failed to load we
 * fall back to "specific media only" (the pre-feature behaviour) rather than
 * showing an option the API would then reject.
 */
export const isAnyMediaAutomationAllowed = (entitlements: any): boolean => {
  const v = entitlements?.any_media_automation_enabled;
  return v === true || v === 'true' || v === 1 || v === '1';
};

/**
 * true when the plan may create automations for this content type ('post' | 'story' | 'live').
 * Backed by the `allowed_content_types` array entitlement — FOC carries 'post' only,
 * every paid/trial plan carries 'post,story,live'.
 *
 * An empty/missing list means "no restriction configured" and allows everything, matching
 * how the clients have always read it, so a plan with no entitlement rows is not silently
 * locked out of Zap entirely.
 */
export const isContentTypeAllowed = (entitlements: any, contentType: string): boolean => {
  const list = entitlements?.allowed_content_types;
  if (!Array.isArray(list) || list.length === 0) return true;
  return list.includes(contentType);
};

/**
 * The surfaces a plan may automate, rendered for a plan/pricing card:
 * "posts", "posts & stories", "posts, stories & live".
 *
 * Derived from isContentTypeAllowed() rather than re-reading the key, so the
 * card and the create-automation gate can never disagree. FOC/Basic carries
 * allowed_content_types = 'post' and prints "posts"; Trial and every paid plan
 * carry 'post,story,live' and print "posts, stories & live". Moving the policy
 * in the DB re-labels the card with no release — the same reason the numeric
 * limits are read from entitlements instead of being hardcoded.
 */
export const formatAutomationSurfaces = (entitlements: any): string => {
  const surfaces = [
    isContentTypeAllowed(entitlements, 'post') ? 'posts' : null,
    isContentTypeAllowed(entitlements, 'story') ? 'stories' : null,
    isContentTypeAllowed(entitlements, 'live') ? 'live' : null,
  ].filter(Boolean) as string[];

  if (surfaces.length === 0) return 'posts';
  if (surfaces.length === 1) return surfaces[0];
  return `${surfaces.slice(0, -1).join(', ')} & ${surfaces[surfaces.length - 1]}`;
};

/**
 * true when the plan allows BUTTON TEMPLATES on a Zap rule — the opener card,
 * the link button on the delivered DM, and the "Ask to Follow" check.
 *
 * The key keeps its original name: it is referenced by plan_entitlements rows in
 * every environment plus user_entitlements_override, and renaming it would buy
 * nothing at runtime. Scope widened 2026-09-01, when the opener card and the
 * link button were decoupled from the follow check.
 * Backed by the `follow_gate_enabled` boolean entitlement — FOC/Basic false,
 * Trial and every paid plan true. Missing reads as FALSE (send immediately).
 */
export const isFollowGateAllowed = (entitlements: any): boolean => {
  const v = entitlements?.follow_gate_enabled;
  return v === true || v === 'true' || v === 1 || v === '1';
};

/**
 * The highest `max_keywords_per_rule` any live plan actually offers.
 *
 * Verified against plan_entitlements: only two values exist across every
 * active plan — 3 (FOC/Basic) and 10 (Monthly, Quarterly, Yearly, Starter,
 * Savings, Special Savings, Trial). So a creator already on 10 is at the
 * product's ceiling and cannot buy their way to more.
 *
 * If a future plan raises the cap, change this one constant.
 */
export const MAX_KEYWORDS_PER_RULE_CEILING = 10;

/**
 * Would upgrading actually get this user more keywords?
 *
 * Used to decide whether the keyword-limit notice offers an "upgrade" link.
 * Offering one to somebody already on the top tier sends them to a page that
 * cannot help — the limit is the same on every plan they could move to.
 */
export const canUpgradeKeywordLimit = (entitlements: any): boolean => {
  const cap = limitAsNumber(entitlements?.max_keywords_per_rule, Infinity);
  // Unlimited (-1) resolves to Infinity — nothing above that either.
  if (!Number.isFinite(cap)) return false;
  return cap < MAX_KEYWORDS_PER_RULE_CEILING;
};
